import {
    filterOptionMatches,
    normalizeFilterOption,
    type FilterOption,
    type FilterOptionItem
} from '../components/filtering/MoreFiltersMenu';

/**
 * The render-ready shape both prop modes of `DataTableFilterDropdown` collapse
 * into. A flat dropdown resolves to exactly one of these with `label: null`;
 * a sectioned one resolves to one per section. Everything downstream is
 * mode-agnostic, so the two variants cannot drift apart visually.
 */
export interface ResolvedFilterSection {
    key: string;
    /** `null` in flat mode - no group header is rendered at all. */
    label: string | null;
    options: readonly FilterOption[];
    multiple: boolean;
    selected: readonly string[];
    /** Single-select commits and closes; multi-select leaves the menu open. */
    closeOnSelect: boolean;
    toggle: (value: string) => void;
    clear: () => void;
}

export interface ResolvedFilterGroup {
    section: ResolvedFilterSection;
    items: readonly FilterOptionItem[];
    /** Index of `items[0]` in the flattened keyboard order across all groups. */
    offset: number;
}

/**
 * Normalizes and search-filters every section, dropping the ones left empty,
 * and stamps each surviving group with its start position in the flattened
 * option order - the index space arrow-key navigation and
 * `aria-activedescendant` both work in.
 */
export function resolveFilterGroups(
    sections: readonly ResolvedFilterSection[],
    query: string
): ResolvedFilterGroup[] {
    const groups: ResolvedFilterGroup[] = [];
    let offset = 0;
    for (const section of sections) {
        const items = section.options
            .map(normalizeFilterOption)
            .filter((item) => filterOptionMatches(item, query));
        if (items.length === 0) {
            continue;
        }
        groups.push({ section, items, offset });
        offset += items.length;
    }
    return groups;
}

export interface FlatFilterOption {
    section: ResolvedFilterSection;
    item: FilterOptionItem;
}

/** The groups' options in keyboard order, so `flat[activeIndex]` is O(1). */
export function flattenFilterGroups(groups: readonly ResolvedFilterGroup[]): FlatFilterOption[] {
    return groups.flatMap((group) => group.items.map((item) => ({ section: group.section, item })));
}
